import 'extensions/ui-components/x-date-input';
import 'extensions/ui-components/x-loader';
import {defineCustomElement} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './x-basket-item.scss?as=stylesheet';

export class BasketItem extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	// eslint-disable-next-line max-lines-per-function
	static get properties () {
		return {
			backorderFormattedDate: {
				attribute: 'backorder-formatted-date',
				type: String
			},
			baseSubtotal: {
				attribute: 'base-subtotal',
				type: Number
			},
			code: {
				type: String
			},
			deliveryDate: {
				attribute: 'delivery-date',
				type: String
			},
			descrip: {
				type: String
			},
			formattedBaseSubtotal: {
				attribute: 'formatted-base-subtotal',
				type: String
			},
			formattedDeliveryDate: {
				attribute: 'formatted-delivery-date',
				type: String
			},
			formattedPrice: {
				attribute: 'formatted-price',
				type: String
			},
			formattedSubtotal: {
				attribute: 'formatted-subtotal',
				type: String
			},
			groupId: {
				attribute: 'group-id',
				type: Number
			},
			hasOptions: {
				attribute: 'has-options',
				type: Boolean
			},
			hideDeliveryDate: {
				attribute: 'hide-delivery-date',
				type: Boolean
			},
			hidePricing: {
				attribute: 'hide-pricing',
				type: Boolean
			},
			hideQuantity: {
				attribute: 'hide-quantity',
				type: Boolean
			},
			hideWishlist: {
				attribute: 'hide-wishlist',
				type: Boolean
			},
			image: {
				type: String
			},
			inventoryStatus: {
				attribute: 'inventory-status',
				type: String
			},
			loading: {
				type: Boolean
			},
			name: {
				type: String
			},
			price: {
				type: Number
			},
			quantity: {
				type: Number
			},
			replacedPart: {
				attribute: 'replaced-part',
				type: String
			},
			subtotal: {
				type: Number
			},
			updateUrl: {
				attribute: 'update-url',
				type: String
			},
			wishlistUrl: {
				attribute: 'wishlist-url',
				type: String
			}
		};
	}

	// eslint-disable-next-line max-lines-per-function
	constructor () {
		super();

		/**
		 * @type {string}
		 */
		this.backorderFormattedDate = '';

		/**
		 * @type {number}
		 */
		this.baseSubtotal = 0;

		/**
		 * @type {string}
		 */
		this.code = '';

		/**
		 * @type {string}
		 */
		this.deliveryDate = '';

		/**
		 * @type {string}
		 */
		this.descrip = '';

		/**
		 * @type {string}
		 */
		this.formattedBaseSubtotal = '';

		/**
		 * @type {string}
		 */
		this.formattedDeliveryDate = '';

		/**
		 * @type {string}
		 */
		this.formattedPrice = '';

		/**
		 * @type {string}
		 */
		this.formattedSubtotal = '';

		/**
		 * @type {number}
		 */
		this.groupId = 0;

		/**
		 * @type {boolean}
		 */
		this.hasOptions = false;

		/**
		 * @type {boolean}
		 */
		this.hideDeliveryDate = false;

		/**
		 * @type {boolean}
		 */
		this.hidePricing = false;

		/**
		 * @type {boolean}
		 */
		this.hideQuantity = false;

		/**
		 * @type {string}
		 */
		this.image = '';

		/**
		 * @type {string}
		 */
		this.inventoryStatus = '';

		/**
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * @type {string}
		 */
		this.name = '';

		/**
		 * @type {number}
		 */
		this.price = 0;

		/**
		 * @type {number}
		 */
		this.quantity = 0;

		/**
		 * @type {string}
		 */
		this.replacedPart = '';

		/**
		 * @type {number}
		 */
		this.subtotal = 0;

		/**
		 * @type {string}
		 */
		this.updateUrl = '';

		/**
		 * @type {string}
		 */
		this.wishlistUrl = '';
	}

	// eslint-disable-next-line max-lines-per-function
	render () {
		const classes = {
			container: true,
			loading: this.loading
		};

		return html`
			<div class="${classMap(classes)}">
				${this.loading ? html`<x-loader class="loader" aspect-ratio-desktop="1600/1200" aspect-ratio-tablet="820/1100" aspect-ratio="390/740"></x-loader>` : nothing}
				<div class="inner-container">
					<div class="info">
						<div class="image">
							<img src="${this.image}" loading="lazy" class="image-source" />
						</div>
						${this.renderInformation()}
					</div>
					<div class="details">
						<div class="actions">
							${this.renderDeliveryDate()}
							${this.renderQuantity()}
							<form action="${this.updateUrl}" method="POST" class="remove-form" @submit="${this.handleRemoveSubmit}">
								<input type="hidden" name="Action" value="RGRP" />
								<input type="hidden" name="Basket_Group" value="${this.groupId}" />
								<button type="submit" class="remove-form-button" aria-label="Remove" @click="${this.handleRemoveButtonClick}"></button>
							</form>
						</div>
						<div class="details-right">
							<div class="pricing-label">Subtotal</div>
							<div class="pricing">
								${this.renderPricing()}
							</div>
							${this.hasOptions ? html`<div class="options"><slot name="option"></slot></div>` : nothing}
						</div>
					</div>
				</div>
				${this.renderWishlistForm()}
			</div>
		`;
	}

	renderDeliveryDate () {
		if (this.hideDeliveryDate && !this.deliveryDate) {
			return nothing;
		}

		return html`
			<form action="${this.updateUrl}" method="POST" class="delivery-date-form" @submit="${this.setLoading}">
				<input type="hidden" name="Action" value="GROUP_DELIVERY_DATE" />
				<input type="hidden" name="Basket_Group" value="${this.groupId}" />
				<input type="hidden" name="Date" value="${this.deliveryDate}" />
				<x-date-input disable-weekends name="DatePicker" value="${this.dateTimeFormat}" @change="${this.handleDateChange}"></x-date-input>
			</form>
		`;
	}

	renderQuantity () {
		if (this.hideQuantity) {
			return html`
				<div class="quantity-form">
					<x-quantity disabled class="quantity quantity-display" name="Quantity" value="${this.quantity}" hide-buttons @change="${this.handleQuantityChange}" disabled></x-quantity>
				</div>
			`;
		}

		return html`
			<form action="${this.updateUrl}" method="POST" class="quantity-form" @submit="${this.setLoading}">
				<input type="hidden" name="Action" value="QTYG" />
				<input type="hidden" name="Basket_Group" value="${this.groupId}" />
				<x-quantity class="quantity" name="Quantity" value="${this.quantity}" hide-buttons-mobile hide-buttons-tablet @change="${this.handleQuantityChange}"></x-quantity>
			</form>
		`;
	}

	renderInformation () {
		return html`
			<div class="info-name">
				${this.renderReplacementPart()}
				${this.renderInventoryStatus()}
				${this.descrip ? html`<div class="name">${this.descrip}</div>` : nothing}
				<div class="code">${this.code}</div>
			</div>
		`;
	}

	renderPricing () {
		if (!this.hidePricing) {
			return html`
				<div class="pricing-subtotal">
					${this.formattedSubtotal}
				</div>
				<div class="pricing-each">
					${this.formattedPrice} ea
				</div>
			`;
		}

		return html`
			<div class="pricing-subtotal">
				<span class="hide-pricing"></span>
			</div>
		`;
	}

	renderWishlistForm () {
		if (this.hideWishlist) {
			return nothing;
		}

		return html`
			<form action="${this.wishlistUrl}" method="POST" class="wishlist-form" @submit="${this.setLoading}">
				<input type="hidden" name="Action" value="MPWL" />
				<input type="hidden" name="Product_Code" value="${this.code}" />
				<input type="hidden" name="Product_Price" value="${this.price}" />
				<input type="hidden" name="Quantity" value="${this.quantity}" />
				<input type="hidden" name="Group_ID" value="${this.groupId}" />
				<button type="submit" class="wishlist-button">Move To Saved List</button>
			</form>
		`;
	}

	renderReplacementPart () {
		if (!this.replacedPart) {
			return nothing;
		}

		return html`
			<div class="replaced-part">
				<span class="replaced-part-icon" aria-hidden="true"></span>
				<span class="replaced-part-text">Replacement Part for:</span>
				${this.replacedPart}
			</div>
		`;
	}

	renderInventoryStatus () {
		if (this.inventoryStatus !== 'BACKORDERED') {
			return nothing;
		}

		return html`
			<div class="inventory-status">
				<span class="inventory-status-icon" aria-hidden="true"></span>
				<span class="inventory-status-text">Backordered</span>
				${this.backorderFormattedDate ? `<span class="inventory-status-eta">ETA: ${this.backorderFormattedDate}</span>` : nothing}
			</div>
		`;
	}

	async handleDateChange (e) {
		const dateElement = e.target;
		let newDate = '';
		if (dateElement?.value !== '') {
			const date = new Date(dateElement?.value);
			newDate = date.toISOString().substring(0, 19);
		}

		if (this.deliveryDate === newDate) {
			return;
		}

		this.deliveryDate = newDate;

		await this.updateComplete;

		dateElement?.form?.requestSubmit();
	}

	handleQuantityChange (e) {
		const [target] = e.composedPath();

		target.form?.requestSubmit();
	}

	handleRemoveButtonClick (e) {
		// eslint-disable-next-line no-alert
		const dialog = window?.confirm('Are you sure you want to remove the Item from your cart?');

		if (!dialog) {
			e?.preventDefault();
		}
	}

	setLoading () {
		this.loading = true;
	}

	get dateTimeFormat () {
		if (!this.deliveryDate) {
			return '';
		}

		const d = new Date(this.deliveryDate);
		return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
	}

	handleRemoveSubmit () {
		this.setLoading();

		window.dataLayer.push({
			ecommerce: null
		});

		window.dataLayer.push({
			ecommerce: {
				items: [
					{
						/* eslint-disable camelcase */
						item_id: this.code,
						item_name: this.name,
						/* eslint-enable camelcase */
						price: this.price,
						quantity: this.quantity
					}
				],
				value: this.subtotal
			},
			event: 'remove_from_cart'
		});
	}
};

defineCustomElement('x-basket-item', BasketItem);
